import React, { useState } from "react";
import TopNavbar from "../components/Nav/TopNavbar";
import Footer from "../components/Sections/Footer";
import SEO from "../components/SEO";
import styled from "styled-components";
import {
  FaGavel as Gavel,
  FaGlobeAfrica as Globe2,
  FaCertificate as BadgeCheck,
  FaEye as Eye,
  FaBullseye as Target,
  FaHandshake as HeartHandshake,
  FaShieldAlt as ShieldCheck,
  FaSearch as Search,
  FaBriefcase as Briefcase,
  FaBuilding as Building2,
  FaBalanceScale as Scale,
} from "react-icons/fa";

import aboutImage from "../assets/img/about1.png";
import aboutImage1 from "../assets/img/about2.png";
import aboutImage2 from "../assets/img/projects/1.png";
import owner1 from "../assets/img/owner1.jpeg";

const CREDENTIAL_TABS = [
  {
    key: "licence",
    icon: BadgeCheck,
    title: "Licence Category",
    text: (
      <>
        Sare Auctioneers is a <strong>Class B licensed auctioneer</strong>,
        licensed by the Kenya Auctioneers Licensing Board.
      </>
    ),
  },
  {
    key: "jurisdiction",
    icon: Gavel,
    title: "Primary Jurisdiction",
    text: (
      <>
        Our auctioneering and related services are undertaken within
        Kenya, including court process serving through our licensed
        auctioneers.
      </>
    ),
  },
  {
    key: "eastAfrica",
    icon: Globe2,
    title: "East African Reach",
    text: (
      <>
        We also undertake services in Uganda, Tanzania, Rwanda and
        Burundi through partnerships with licensed auctioneers in
        those countries.
      </>
    ),
  },
];

export default function AboutUs() {
  const [activeCredential, setActiveCredential] = useState("jurisdiction");

  return (
    <>
      <SEO
        title="About Us | Sare Auctioneers"
        description="Learn about Sare Auctioneers, our history, services, vision, mission, values and ownership structure."
      />

      <TopNavbar />

      <Page>
        {/* HERO */}
        <Hero>
          <div className="container">
            <HeroContent>
              <div>
                <p className="eyebrow">ABOUT SARE AUCTIONEERS</p>
                <h1>Professional Auctioneering. Trusted Service.</h1>
                <p className="heroText">
                  Sare Auctioneers is a licensed Class B auctioneering firm
                  established in 2015, committed to providing efficient,
                  professional and reliable auctioneering and related services
                  to clients and business partners.
                </p>
              </div>

              <HeroImage>
                <img src={aboutImage} alt="Sare Auctioneers" />
              </HeroImage>
            </HeroContent>
          </div>
        </Hero>

        <div className="container">
          {/* ABOUT THE COMPANY */}
          <Section>
            <SectionHeader>
              <p className="eyebrow">ABOUT THE COMPANY</p>
              <h2>Who We Are</h2>
            </SectionHeader>

            <TwoColumn>
              <ImageCard>
                <img src={aboutImage1} alt="About Sare Auctioneers" />
              </ImageCard>

              <TextContent>
                <p>
                  Sare Auctioneers is an auctioneering firm licensed by the
                  Kenya Auctioneers Licensing Board to undertake public
                  auctions, distress for rent, realization of charged
                  securities, execution of court orders, court eviction
                  orders, and repossessions of chattels mortgage.
                </p>

                <p>
                  We also undertake the above services in the East African
                  countries of Uganda, Tanzania, Rwanda and Burundi in
                  partnership with licensed auctioneers in the named countries.
                </p>

                <p>
                  Our auctioneers are also licensed as Kenya court process
                  servers of the High Court of Kenya. In addition to the above,
                  we undertake debt collection services as well as private
                  investigation (PI) services within Kenya and East Africa.
                </p>

                <p>
                  Sare Auctioneers was registered in 2015 and has since acquired
                  a Class B licence. The business is established with a firm
                  commitment to efficient service and customer satisfaction.
                </p>
              </TextContent>
            </TwoColumn>
          </Section>

          {/* LICENCE & JURISDICTION */}
          <Section className="lightSection">
            <SectionHeader center>
              <p className="eyebrow">OUR CREDENTIALS</p>
              <h2>Licence & Jurisdiction</h2>
              <p>
                Our operations are founded on professional licensing and
                experience in the auctioneering and related services sector.
              </p>
            </SectionHeader>

            <InfoGrid>
              {CREDENTIAL_TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeCredential === tab.key;

                return (
                  <InfoCard
                    key={tab.key}
                    type="button"
                    active={isActive}
                    aria-pressed={isActive}
                    onClick={() => setActiveCredential(tab.key)}
                  >
                    <IconBox>
                      <Icon size={26} />
                    </IconBox>

                    <h3>{tab.title}</h3>

                    <p>{tab.text}</p>
                  </InfoCard>
                );
              })}
            </InfoGrid>

            {activeCredential === "jurisdiction" && (
              // TODO: item 2's wording is cut off in the screenshot after
              // "...throughout the Republic of Kenya." on item 1 - send the
              // full text and I'll drop it in below.
              <ObjectsSection>
                <h3>To Carry on the Business Of</h3>
                <ObjectsList>
                  <li>
                    Repossession, realization of securities, sale of
                    moveable and immoveable property by auction or any
                    other mode of sale by competition, throughout the
                    Republic of Kenya.
                  </li>
                  <li>
                    Excecution of court orders and distress for rent in the <b> COUNTIES </b> of
                    <b> NAIROBI</b>, <b> KIAMBU</b>,  <b> NAKURU</b>,  <b> KAJIADO</b>,
                    <b> KERICHO</b>,  <b> KISUMU </b>
                  </li>
                </ObjectsList>
              </ObjectsSection>
            )}
          </Section>

          {/* VISION MISSION VALUES */}
          <Section>
            <SectionHeader>
              <p className="eyebrow">WHAT MOTIVATES US</p>
              <h2>Our Vision, Mission & Values</h2>
            </SectionHeader>

            <MotivationGrid>
              <MotivationCard>
                <IconBox>
                  <Eye size={26} />
                </IconBox>

                <div>
                  <h3>Vision</h3>
                  <p>
                    To be the market leader in the auctioneering field within
                    the East African Community.
                  </p>
                </div>
              </MotivationCard>

              <MotivationCard>
                <IconBox>
                  <Target size={26} />
                </IconBox>

                <div>
                  <h3>Mission</h3>
                  <p>
                    To meet and exceed the ever growing needs of our clients
                    and partners in business.
                  </p>
                </div>
              </MotivationCard>
            </MotivationGrid>

            <ValuesSection>
              <h3>Our Values</h3>
              <p className="valuesIntro">
                The company upholds the following values as a guideline to its
                business operations:
              </p>

              <ValuesGrid>
                <Value>
                  <ValueIcon>
                    <HeartHandshake size={22} />
                  </ValueIcon>
                  <strong>Respect</strong>
                </Value>

                <Value>
                  <ValueIcon>
                    <ShieldCheck size={22} />
                  </ValueIcon>
                  <strong>Integrity</strong>
                </Value>

                <Value>
                  <ValueIcon>
                    <HeartHandshake size={22} />
                  </ValueIcon>
                  <strong>Customer Care</strong>
                </Value>

                <Value>
                  <ValueIcon>
                    <BadgeCheck size={22} />
                  </ValueIcon>
                  <strong>Accountability</strong>
                </Value>

                <Value>
                  <ValueIcon>
                    <Briefcase size={22} />
                  </ValueIcon>
                  <strong>Professionalism</strong>
                </Value>
              </ValuesGrid>
            </ValuesSection>
          </Section>

          {/* SERVICES */}
          <Section className="lightSection">
            <SectionHeader>
              <p className="eyebrow">WHAT WE DO</p>
              <h2>Our Areas of Service</h2>
              <p>
                Sare Auctioneers provides a range of auctioneering and related
                professional services.
              </p>
            </SectionHeader>

            <ServicesGrid>
              <ServiceItem>
                <ServiceIcon>
                  <Gavel size={22} />
                </ServiceIcon>
                <span>Public Auctions</span>
              </ServiceItem>

              <ServiceItem>
                <ServiceIcon>
                  <Building2 size={22} />
                </ServiceIcon>
                <span>Distress for Rent</span>
              </ServiceItem>

              <ServiceItem>
                <ServiceIcon>
                  <BadgeCheck size={22} />
                </ServiceIcon>
                <span>Realisation of Charged Securities</span>
              </ServiceItem>

              <ServiceItem>
                <ServiceIcon>
                  <Scale size={22} />
                </ServiceIcon>
                <span>Execution of Court Orders</span>
              </ServiceItem>

              <ServiceItem>
                <ServiceIcon>
                  <Building2 size={22} />
                </ServiceIcon>
                <span>Court Eviction Orders</span>
              </ServiceItem>

              <ServiceItem>
                <ServiceIcon>
                  <Gavel size={22} />
                </ServiceIcon>
                <span>Repossessions of Chattels & Mortgage</span>
              </ServiceItem>

              <ServiceItem>
                <ServiceIcon>
                  <Briefcase size={22} />
                </ServiceIcon>
                <span>Court Process Serving</span>
              </ServiceItem>

              <ServiceItem>
                <ServiceIcon>
                  <HeartHandshake size={22} />
                </ServiceIcon>
                <span>Debt Collection</span>
              </ServiceItem>

              <ServiceItem>
                <ServiceIcon>
                  <Search size={22} />
                </ServiceIcon>
                <span>Private Investigations</span>
              </ServiceItem>
            </ServicesGrid>

            <CapabilityText>
              <strong>
                Auctioneers, Debt Collection, Official Brokers, Repossessors,
                Process Servers, Private Investigators and Bailiffs Commission
                Agents
              </strong>
            </CapabilityText>
          </Section>

          {/* OWNERSHIP */}
          <Section>
            <SectionHeader>
              <p className="eyebrow">COMPANY STRUCTURE</p>
              <h2>Ownership & Management</h2>
            </SectionHeader>

            <OwnershipContent>
              <OwnershipImage>
                <img
                  src={owner1}
                  alt="Sare Auctioneers company structure"
                />
              </OwnershipImage>

              <TextContent>
                <p>
                  Sare Auctioneers was established in 2015 under the Business
                  Name Act by <strong>Mr. Kephers Usenge</strong>, who is a
                   licensed auctioneer.
                </p>

                <p>
                  Mr. Usenge is currently pursuing his PhD studies in Leadership
                  and Governance. He is a holder of an MBA degree obtained from
                  the University of Nairobi in 1987 and a BCom (First Class)
                  degree obtained from the same university in 1985.
                </p>

                <p>
                  He is an associate of the Chartered Insurance Institute (UK)
                  and a Chartered Insurance Practitioner (UK).
                </p>

                <p>
                  Mr. Usenge has worked in several countries including Rwanda,
                  Tanzania, Uganda and Kenya. Notably, he has been a member of
                  various technical working groups at EAC level on insurance
                  education.
                </p>
              </TextContent>
            </OwnershipContent>
          </Section>

          {/* MANAGEMENT TEAM */}
          <Section className="lightSection">
            <ManagementContent>
              <TextContent>
                <p>
                  <strong>Mr. Allan Arina</strong> serves as the Operations
                  and Marketing Manager at Sare Auctioneers, bringing over 10
                  years of experience in the auctioneering field.
                </p>
              </TextContent>

              <OwnershipImage>
                <img src={aboutImage2} alt="Allan Arina" />
              </OwnershipImage>
            </ManagementContent>
          </Section>

        </div>
      </Page>

      <Footer />
    </>
  );
}

/* =========================
   PAGE
========================= */

const Page = styled.main`
  min-height: 70vh;
  color: #3b1718;

  .container {
    width: 100%;
  }

  @media (max-width: 768px) {
    .container {
      padding-left: 20px;
      padding-right: 20px;
    }
  }
`;

/* =========================
   HERO
========================= */

const Hero = styled.section`
  padding: 150px 0 90px;
  background: #f8f6f5;

  @media (max-width: 768px) {
    padding: 120px 0 60px;
  }
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.9fr;
  gap: 70px;
  align-items: center;

  h1 {
    max-width: 700px;
    margin: 10px 0 25px;
    font-size: 58px;
    line-height: 1.08;
    font-weight: 800;
    color: #3b1718;
  }

  .heroText {
    max-width: 650px;
    font-size: 18px;
    line-height: 1.8;
    color: #5d191a;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;

    h1 {
      font-size: 44px;
    }
  }
`;

const HeroImage = styled.div`
  height: 430px;
  border-radius: 16px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    height: 300px;
  }
`;

/* =========================
   SECTIONS
========================= */

const Section = styled.section`
  padding: 100px 0;

  &.lightSection {
    background: #f8f6f5;
    padding-left: 40px;
    padding-right: 40px;
    border-radius: 16px;
  }

  @media (max-width: 768px) {
    padding: 65px 0;

    &.lightSection {
      padding-left: 20px;
      padding-right: 20px;
      border-radius: 10px;
    }
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 50px;

  ${(props) =>
    props.center &&
    `
      text-align: center;
      margin-left: auto;
      margin-right: auto;
      max-width: 750px;
  `}

  h2 {
    margin: 8px 0 15px;
    font-size: 42px;
    line-height: 1.2;
    color: #3b1718;
  }

  p:not(.eyebrow) {
    max-width: 760px;
    line-height: 1.8;
    color: #6b4a4a;
  }

  ${(props) =>
    props.center &&
    `
      p:not(.eyebrow) {
        margin-left: auto;
        margin-right: auto;
      }
  `}

  .eyebrow {
    margin: 0;
    color: #8a3638;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 2px;
  }

  @media (max-width: 768px) {
    h2 {
      font-size: 34px;
    }
  }
`;

/* =========================
   TWO COLUMN
========================= */

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 35px;
  }
`;

const ImageCard = styled.div`
  height: 470px;
  overflow: hidden;
  border-radius: 14px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    height: 320px;
  }
`;

const TextContent = styled.div`
  color: #5d191a;
  font-size: 16px;
  line-height: 1.85;

  p {
    margin: 0 0 22px;
  }

  strong {
    color: #3b1718;
  }
`;

/* =========================
   INFO CARDS
========================= */

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 25px;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  width: 100%;
  padding: 35px;
  background: white;
  border: 2px solid ${(props) => (props.active ? "#8a3638" : "transparent")};
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  box-shadow: ${(props) =>
    props.active ? "0 8px 24px rgba(138, 54, 56, 0.15)" : "none"};

  &:hover {
    border-color: #8a3638;
  }

  h3 {
    margin: 15px 0;
    font-size: 22px;
    color: #3b1718;
  }

  p {
    margin: 0;
    color: #6b4a4a;
    line-height: 1.7;
  }

  strong {
    color: #3b1718;
  }
`;

const IconBox = styled.div`
  width: 54px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #8a3638;
  color: #fff;
  border-radius: 10px;
`;

const ObjectsSection = styled.div`
  max-width: 800px;
  margin: 45px auto 0;

  h3 {
    margin: 0 0 15px;
    font-size: 24px;
    color: #3b1718;
    text-align: center;
  }
`;

const ObjectsList = styled.ol`
  margin: 0;
  padding-left: 22px;
  color: #6b4a4a;
  line-height: 1.8;

  li {
    margin-bottom: 12px;
  }

  li:last-child {
    margin-bottom: 0;
  }
`;

/* =========================
   VISION / MISSION
========================= */

const MotivationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 25px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const MotivationCard = styled.div`
  display: flex;
  gap: 25px;
  padding: 35px;
  background: #f8f6f5;
  border-radius: 12px;

  h3 {
    margin: 0 0 10px;
    font-size: 24px;
    color: #3b1718;
  }

  p {
    margin: 0;
    line-height: 1.7;
    color: #6b4a4a;
  }
`;

const ValuesSection = styled.div`
  margin-top: 45px;

  h3 {
    margin: 0 0 10px;
    font-size: 28px;
    color: #3b1718;
  }

  .valuesIntro {
    color: #6b4a4a;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 15px;
  margin-top: 25px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const Value = styled.div`
  padding: 25px 20px;
  border: 1px solid #eadede;
  border-radius: 10px;
  background: white;

  strong {
    display: block;
    margin-top: 15px;
    color: #3b1718;
  }
`;

const ValueIcon = styled.div`
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3e4e4;
  color: #8a3638;
  border-radius: 9px;
`;

/* =========================
   SERVICES
========================= */

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;

  @media (max-width: 850px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 550px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 9px;
  color: #3b1718;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const ServiceIcon = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3e4e4;
  color: #8a3638;
  border-radius: 8px;
`;

const CapabilityText = styled.p`
  margin: 35px auto 0;
  max-width: 900px;
  text-align: center;
  line-height: 1.7;
  color: #5d191a;
`;

/* =========================
   OWNERSHIP
========================= */

const OwnershipContent = styled.div`
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`;

const OwnershipImage = styled.div`
  width: 100%;
  max-width: 380px;
  height: 480px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 16px;
  background: #f3eeee;
  box-shadow: 0 12px 35px rgba(59, 23, 24, 0.12);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    display: block;
  }

  @media (max-width: 850px) {
    max-width: 340px;
    height: 430px;
  }

  @media (max-width: 500px) {
    max-width: 100%;
    height: 420px;
  }
`;

/* =========================
   MANAGEMENT TEAM
========================= */

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  max-width: 400px;
  margin: 0 auto;
`;

const TeamCard = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(59, 23, 24, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(59, 23, 24, 0.12);
  }
`;

const TeamImage = styled.div`
  height: 260px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TeamInfo = styled.div`
  padding: 25px;

  h3 {
    margin: 0 0 4px;
    font-size: 20px;
    color: #3b1718;
  }

  p {
    margin: 12px 0 0;
    line-height: 1.7;
    color: #6b4a4a;
    font-size: 15px;
  }
`;

const TeamRole = styled.span`
  display: inline-block;
  font-size: 13px;
  font-weight: 700;
  color: #8a3638;
  letter-spacing: 0.3px;
`;

const ManagementContent = styled(OwnershipContent)`
  grid-template-columns: 1.2fr 0.8fr;
`;